import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import FileDropZone from "../components/materials/FileDropZone";
import MaterialEditDialog from "../components/materials/MaterialEditDialog";
import MaterialList from "../components/materials/MaterialList";
import MaterialUploadDialog from "../components/materials/MaterialUploadDialog";
import MaterialsPageHeader from "../components/materials/MaterialsPageHeader";
import {
  selectMaterialCategories,
  selectMaterials,
  selectMaterialsUploadProgress,
  selectMaterialsUploadStatus,
} from "../features/materials/materials-slice";
import {
  downloadMaterial,
  fetchMaterialCategories,
  fetchMaterials,
  MaterialAudienceFilter,
} from "../features/materials/slice-extensions/materials-slice-extension";
import { useMaterialActions } from "../features/materials/use-material-actions";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { selectGroups, selectMembers } from "../features/members/members-slice";
import { fetchGroups, fetchMembers } from "../features/members/slice-extensions/members-slice-extension";

/** Org-shared files: upload (button or drag & drop), audience, search, download. */
const MaterialsPage = () => {
  const dispatch = useAppDispatch();
  const materials = useAppSelector(selectMaterials);
  const categories = useAppSelector(selectMaterialCategories);
  const uploadStatus = useAppSelector(selectMaterialsUploadStatus);
  const uploadProgress = useAppSelector(selectMaterialsUploadProgress);
  const members = useAppSelector(selectMembers);
  const groups = useAppSelector(selectGroups);
  const [category, setCategory] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [audience, setAudience] = useState<MaterialAudienceFilter>({ kind: "all" });
  const actions = useMaterialActions();

  // Debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      dispatch(
        fetchMaterials({
          category: category ?? undefined,
          search: search || undefined,
          audience,
        }),
      );
    }, 250);
    return () => clearTimeout(handle);
  }, [dispatch, category, search, audience]);

  useEffect(() => {
    dispatch(fetchMaterialCategories());
    dispatch(fetchMembers());
    dispatch(fetchGroups());
  }, [dispatch]);

  const memberNameById = useMemo(
    () => new Map(members.map((member) => [member.id, `${member.firstName} ${member.lastName}`])),
    [members],
  );
  const groupNameById = useMemo(
    () => new Map(groups.map((group) => [group.id, group.name])),
    [groups],
  );

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full min-w-0 p-4 pb-10 lg:px-10">
          <MaterialsPageHeader
            categories={categories}
            activeCategory={category}
            search={search}
            audience={audience}
            members={members}
            groups={groups}
            uploading={uploadStatus === LoadingStatus.Loading}
            onCategoryChange={setCategory}
            onSearchChange={setSearch}
            onAudienceChange={setAudience}
            onFilePicked={actions.setPendingFile}
          />

          <FileDropZone
            disabled={uploadStatus === LoadingStatus.Loading}
            onFile={actions.setPendingFile}
          />

          <MaterialList
            materials={materials}
            memberNameById={memberNameById}
            groupNameById={groupNameById}
            onDownload={(material) => dispatch(downloadMaterial(material.id))}
            onEdit={actions.setEditing}
            onDelete={actions.remove}
          />
        </div>
      </div>

      {actions.pendingFile && (
        <MaterialUploadDialog
          file={actions.pendingFile}
          categories={categories}
          initialCategory={category}
          members={members}
          groups={groups}
          busy={uploadStatus === LoadingStatus.Loading}
          progress={uploadProgress}
          failed={uploadStatus === LoadingStatus.Failed}
          onClose={() => actions.setPendingFile(null)}
          onSubmit={actions.submitUpload}
        />
      )}

      {actions.editing && (
        <MaterialEditDialog
          material={actions.editing}
          categories={categories}
          members={members}
          groups={groups}
          busy={actions.busy}
          onClose={() => actions.setEditing(null)}
          onSubmit={actions.submitEdit}
        />
      )}
    </section>
  );
};

export default MaterialsPage;
